export const cleanFalsy = obj =>
  Object.entries(obj).reduce((a, [k, v]) => (v ? ((a[k] = v), a) : a), {});

export const cleanNullUndefined = obj =>
  Object.entries(obj).reduce(
    (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
    {}
  );

export const cleanNull = obj =>
  Object.entries(obj).reduce(
    (a, [k, v]) => (v === null ? a : ((a[k] = v), a)),
    {}
  );

export const cleanUndefined = obj =>
  Object.entries(obj).reduce(
    (a, [k, v]) => (v === undefined ? a : ((a[k] = v), a)),
    {}
  );

export const sortByKey = unordered =>
  Object.keys(unordered)
    .sort()
    .reduce((obj, key) => {
      obj[key] = unordered[key];
      return obj;
    }, {});

/**
 * Deep merge with 0/1 pair guarantee on a single call.
 *
 * Behavior:
 * - Only updates keys present in "patch"; others remain unchanged.
 * - If you submit either "0" or "1" for an item, both "0" and "1" will exist after merging.
 * - Submitting only "2" does not add "0"/"1".
 *
 * Options:
 * - arrayStrategy: 'replace' | 'mergeByIndex' (default 'replace')
 * - ignoreUndefined: skip undefined values from patch (default true)
 * - ignoreNull: skip null values from patch (default false)
 * - pairKeys: keys treated as a pair (default ['0', '1'])
 * - pairDefaults: default values for missing pair keys (default { '0': [], '1': [] })
 *
 * @param {object} base
 * @param {object} patch
 * @param {object} [options]
 * @returns {object} merged result
 */
export function updateData(base, patch, options = {}) {
  const {
    arrayStrategy = 'replace',
    ignoreUndefined = true,
    ignoreNull = false,
    pairKeys = ['0', '1'],
    pairDefaults = { 0: [], 1: [] } // customize e.g. { '0': [''], '1': [''] }
  } = options;

  // Track which top-level IDs in the patch submitted either "0" or "1"
  const idsNeedingPair = new Set();
  if (isPlainObject(patch)) {
    for (const id of Object.keys(patch)) {
      const p = patch[id];
      if (!isPlainObject(p)) continue;
      const submittedAnyPairKey = pairKeys.some(k =>
        Object.prototype.hasOwnProperty.call(p, k)
      );
      if (submittedAnyPairKey) idsNeedingPair.add(id);
    }
  }

  // Deep-merge "patch" into "base" immutably
  const merged = deepMerge(base, patch, {
    arrayStrategy,
    ignoreUndefined,
    ignoreNull
  });

  // Enforce the 0/1 pair only for IDs that submitted either "0" or "1"
  for (const id of idsNeedingPair) {
    const target = merged && merged[id];
    if (!isPlainObject(target)) continue;
    for (const k of pairKeys) {
      if (!Object.prototype.hasOwnProperty.call(target, k)) {
        const def = pairDefaults[k];
        target[k] = cloneDefault(def);
      }
    }
  }

  return merged;
}

/* ----------------- helpers ----------------- */

function deepMerge(base, patch, options) {
  const { arrayStrategy, ignoreUndefined, ignoreNull } = options;

  if (patch === base) return base;

  if (Array.isArray(patch)) {
    if (!Array.isArray(base)) return patch.slice();
    if (arrayStrategy === 'replace') return patch.slice();

    // mergeByIndex
    const max = Math.max(base.length, patch.length);
    const out = new Array(max);
    for (let i = 0; i < max; i++) {
      if (i in patch) {
        const pv = patch[i];
        if (pv === undefined && ignoreUndefined) {
          out[i] = base[i];
        } else if (isPlainObject(pv) || Array.isArray(pv)) {
          out[i] = deepMerge(base[i], pv, options);
        } else if (pv === null && ignoreNull) {
          out[i] = base[i];
        } else {
          out[i] = pv;
        }
      } else {
        out[i] = base[i];
      }
    }
    return out;
  }

  if (isPlainObject(patch)) {
    const out = isPlainObject(base) ? { ...base } : {};
    for (const key of Object.keys(patch)) {
      const pv = patch[key];

      if (pv === undefined && ignoreUndefined) continue;

      const bv = out[key];

      if (isPlainObject(pv) || Array.isArray(pv)) {
        out[key] = deepMerge(bv, pv, options);
      } else if (pv === null && ignoreNull) {
        continue;
      } else {
        out[key] = pv;
      }
    }
    return out;
  }

  // Primitive values
  if (patch === undefined && ignoreUndefined) return base;
  if (patch === null && ignoreNull) return base;
  return patch;
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function cloneDefault(val) {
  if (Array.isArray(val)) return val.slice();
  if (isPlainObject(val)) return { ...val };
  return val;
}
